import { formatDuration } from '@room-verify/shared';
import type { InspectionListItem } from '../api';
import { ar, arDate, arTime } from '../ar';
import { IconArrowRight, IconCamera, IconClock, IconPencil, IconUser } from './icons';
import { Chip, Skeleton, StatusIndicator } from './primitives';

/**
 * One dataset, two presentations. Below 1024px the table is not shrunk — it does not
 * exist. CSS decides which of the two is in the layout, so a phone gets a card designed
 * as a card rather than a row squeezed into one.
 */

function TypeMark({ type }: { type: 'CHECKOUT' | 'CLEAN_ROOM' }) {
  // A dot plus a word rather than a coloured pill: forty rows of pills is noise.
  return (
    <StatusIndicator tone={type === 'CLEAN_ROOM' ? 'accent' : 'neutral'}>
      {type === 'CHECKOUT' ? ar.checkout : ar.clean}
    </StatusIndicator>
  );
}

function ItemsMark({ item }: { item: InspectionListItem }) {
  if (!item.itemsComplete) {
    const count = item.missingItems.length + (item.otherMissingItem ? 1 : 0);
    return (
      <Chip tone="warning">
        {ar.missingItemsBadge} <span className="num">{count}</span>
      </Chip>
    );
  }
  return <span className="cell-quiet">{ar.itemsCompleteShort}</span>;
}

/* ---------------------------------------------------------------- table */

export function InspectionTable({
  items,
  onOpen,
  onEdit,
}: {
  items: InspectionListItem[];
  onOpen: (id: string) => void;
  onEdit: (item: InspectionListItem) => void;
}) {
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">{ar.roomNumber}</th>
            <th scope="col">{ar.inspectionType}</th>
            <th scope="col">{ar.supervisor}</th>
            <th scope="col">{ar.completedAt}</th>
            <th scope="col" className="col-num">
              {ar.duration}
            </th>
            <th scope="col" className="col-num">
              {ar.photos}
            </th>
            <th scope="col">{ar.roomItems}</th>
            <th scope="col" className="col-actions">
              <span className="sr-only">{ar.actions}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.id}
              className="data-row"
              onClick={() => onOpen(item.id)}
              tabIndex={0}
              role="link"
              aria-label={`${ar.openReport} ${item.roomNumber}`}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onOpen(item.id);
                }
              }}
            >
              <td>
                <span className="cell-room num" dir="ltr">
                  {item.roomNumber}
                </span>
              </td>
              <td>
                <TypeMark type={item.inspectionType} />
              </td>
              <td className="cell-truncate">{item.supervisorName}</td>
              <td className="num">{arTime(item.completedAt)}</td>
              <td className="num col-num">{formatDuration(item.durationSeconds)}</td>
              <td className="num col-num">{item.photoCount}</td>
              <td>
                <span className="cell-flags">
                  <ItemsMark item={item} />
                  {item.editCount > 0 && <Chip tone="accent">{ar.edited}</Chip>}
                </span>
              </td>
              <td className="col-actions">
                <span className="row-actions">
                  <button
                    className="icon-btn icon-btn-sm"
                    onClick={(event) => {
                      event.stopPropagation();
                      onEdit(item);
                    }}
                    aria-label={`${ar.edit} ${item.roomNumber}`}
                  >
                    <IconPencil size={15} />
                  </button>
                  <IconArrowRight size={15} className="row-chevron flip-rtl" />
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ----------------------------------------------------------------- card */

export function InspectionCard({
  item,
  onOpen,
  onEdit,
}: {
  item: InspectionListItem;
  onOpen: (id: string) => void;
  onEdit: (item: InspectionListItem) => void;
}) {
  return (
    <li className="insp-card">
      <button
        className="insp-card-main"
        onClick={() => onOpen(item.id)}
        aria-label={`${ar.openReport} ${item.roomNumber}`}
      >
        <span className="insp-card-head">
          <span className="insp-card-room">
            <span className="insp-card-room-label">{ar.room}</span>
            <span className="insp-card-room-number num" dir="ltr">
              {item.roomNumber}
            </span>
          </span>
          <TypeMark type={item.inspectionType} />
        </span>

        <span className="insp-card-meta">
          <span className="meta-part">
            <IconUser size={13} />
            {item.supervisorName}
          </span>
          <span className="meta-dot" aria-hidden="true" />
          <span className="meta-part num">
            <IconClock size={13} />
            {arTime(item.completedAt)}
          </span>
          <span className="meta-dot" aria-hidden="true" />
          <span className="meta-part num">{formatDuration(item.durationSeconds)}</span>
          <span className="meta-dot" aria-hidden="true" />
          <span className="meta-part num">
            <IconCamera size={13} />
            {item.photoCount}
          </span>
        </span>

        <span className="insp-card-foot">
          <ItemsMark item={item} />
          {item.editCount > 0 && <Chip tone="accent">{ar.edited}</Chip>}
          <span className="insp-card-open">
            {ar.openReport}
            <IconArrowRight size={14} className="flip-rtl" />
          </span>
        </span>
      </button>

      <button
        className="insp-card-edit"
        onClick={() => onEdit(item)}
        aria-label={`${ar.edit} ${item.roomNumber}`}
      >
        <IconPencil size={15} />
      </button>
    </li>
  );
}

/* ----------------------------------------------------------------- list */

export function InspectionList({
  items,
  onOpen,
  onEdit,
  groupByDate = false,
}: {
  items: InspectionListItem[];
  onOpen: (id: string) => void;
  onEdit: (item: InspectionListItem) => void;
  groupByDate?: boolean;
}) {
  if (!groupByDate) {
    return (
      <>
        <InspectionTable items={items} onOpen={onOpen} onEdit={onEdit} />
        <ul className="insp-cards">
          {items.map((item) => (
            <InspectionCard key={item.id} item={item} onOpen={onOpen} onEdit={onEdit} />
          ))}
        </ul>
      </>
    );
  }

  // Archive view: a date rule between days gives the list rhythm without boxes.
  const groups = new Map<string, InspectionListItem[]>();
  for (const item of items) {
    const bucket = groups.get(item.businessDate) ?? [];
    bucket.push(item);
    groups.set(item.businessDate, bucket);
  }

  return (
    <div className="insp-groups">
      {[...groups.entries()].map(([date, group]) => (
        <div key={date} className="insp-group">
          <div className="insp-group-head">
            <span className="insp-group-date">{arDate(`${date}T09:00:00.000Z`)}</span>
            <span className="insp-group-rule" aria-hidden="true" />
            <span className="insp-group-count num">{group.length}</span>
          </div>
          <InspectionTable items={group} onOpen={onOpen} onEdit={onEdit} />
          <ul className="insp-cards">
            {group.map((item) => (
              <InspectionCard key={item.id} item={item} onOpen={onOpen} onEdit={onEdit} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- skeleton */

/** Shaped like the rows that are coming, so the layout does not jump. */
export function InspectionListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="list-skeleton">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="list-skeleton-row">
          <Skeleton height={18} width={54} />
          <Skeleton height={14} width={72} />
          <Skeleton height={14} width={110} />
          <Skeleton height={14} width={64} />
        </div>
      ))}
    </div>
  );
}
